import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { AlertCircle, RotateCw } from 'lucide-react';

export interface BankSearchProcessingProps {
  pollerState: 'idle' | 'polling' | 'slow' | 'timeout' | 'completed' | 'error';
  errorMessage: string | null;
  onRetry: () => void;
  searchTitle: string | null;
}

const MESSAGES = [
  'Reading the role…',
  'Extracting skills…',
  "Mapping your talent pool's wording…",
];

export const BankSearchProcessing: React.FC<BankSearchProcessingProps> = ({
  pollerState,
  errorMessage,
  onRetry,
  searchTitle,
}) => {
  const [msgIndex, setMsgIndex] = useState(0);

  useEffect(() => {
    if (pollerState === 'polling') {
      const interval = setInterval(() => {
        setMsgIndex((prev) => (prev + 1) % MESSAGES.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [pollerState]);

  if (pollerState === 'idle' || pollerState === 'completed') {
    return null;
  }

  return (
    <Paper
      variant='outlined'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        p: 6,
        borderRadius: 3,
        bgcolor: '#ffffff',
        borderColor: 'divider',
        textAlign: 'center',
      }}
    >
      {searchTitle && (
        <Typography variant='subtitle1' fontWeight={700} color='text.primary' sx={{ mb: 3 }}>
          {searchTitle}
        </Typography>
      )}

      {pollerState === 'error' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: 420 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: '#fee2e2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#dc2626',
              mb: 1.5,
            }}
          >
            <AlertCircle size={24} />
          </Box>
          <Typography variant='body2' fontWeight={500} color='text.secondary' sx={{ mb: 2 }}>
            {errorMessage || 'An error occurred during processing'}
          </Typography>
          <Button
            variant='contained'
            color='error'
            onClick={onRetry}
            startIcon={<RotateCw size={14} />}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2 }}
          >
            Try Again
          </Button>
        </Box>
      )}

      {pollerState === 'timeout' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: 420 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: '#fef3c7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#d97706',
              mb: 1.5,
            }}
          >
            <AlertCircle size={24} />
          </Box>
          <Typography variant='body2' fontWeight={500} color='text.secondary' sx={{ mb: 2 }}>
            Processing timed out. Please try again.
          </Typography>
          <Button
            variant='contained'
            onClick={onRetry}
            startIcon={<RotateCw size={14} />}
            sx={{
              bgcolor: '#d97706',
              '&:hover': { bgcolor: '#b45309' },
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
            }}
          >
            Try Again
          </Button>
        </Box>
      )}

      {(pollerState === 'polling' || pollerState === 'slow') && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
          <CircularProgress size={36} sx={{ color: '#6366f1', mb: 2 }} />
          <Typography variant='body2' fontWeight={500} color='text.secondary'>
            {pollerState === 'slow'
              ? 'Still working — this is taking longer than usual…'
              : MESSAGES[msgIndex]}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};
